'use client';

import { Button } from '@app/app/_components/Button/Button';
import { Input } from '@app/app/_components/Input/Input';
import { Select } from '@app/app/_components/Input/Select/Select';
import { Message } from '@app/app/_components/Message/Message';
import { SpinnerIcon } from '@app/app/_icons/SpinnerIcon';
import { AuthAction } from '@app/domain/model/AuthAction';
import { FormStatus } from '@app/lib/form/contracts/FormStatus';
import { sendVerificationCode } from '@app/service/auth/actions/sendVerificationCode';
import { loginWeakAuth } from '@app/service/auth/actions/loginWeakAuth';
import { registerWeakAuth } from '@app/service/auth/actions/registerWeakAuth';
import { useTranslations } from 'next-intl';
import { useCallback, useRef, useState } from 'react';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';

interface Props {
    onSuccess?: () => void;
    disableAutoFill?: boolean;
    disablePasting?: boolean;
}

enum Phase {
    Form = 'form',
    Verification = 'verification',
}

interface FormData {
    action: AuthAction;
    cardNumber: string;
    personalCode: string;
    phoneNumber: string;
}

export function TwoFactorLoginSection({ onSuccess, disableAutoFill = false, disablePasting = false }: Props) {
    const t = useTranslations();

    const autoComplete = disableAutoFill ? 'off' : undefined;

    const [phase, setPhase] = useState<Phase>(Phase.Form);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
    const [successMessage, setSuccessMessage] = useState<Nullable<string>>(null);
    const [formData, setFormData] = useState<FormData>({
        action: AuthAction.Login,
        cardNumber: '',
        personalCode: '',
        phoneNumber: '',
    });
    const [smsCode, setSmsCode] = useState('');

    const handleReset = useCallback(() => {
        setPhase(Phase.Form);
        setSmsCode('');
        setIsLoading(false);
        setSuccessMessage(null);
    }, []);

    const handleCancel = useCallback(
        (message: Nullable<string>) => {
            setErrorMessage(message);
            handleReset();
        },
        [handleReset],
    );

    const isSendingRef = useRef(false);

    const handleSendCode = useCallback(
        async (e?: React.MouseEvent) => {
            if (isSendingRef.current) return;

            e?.preventDefault();
            e?.stopPropagation();

            const cardNumber = formData.cardNumber.length === 6 ? `0${formData.cardNumber}` : formData.cardNumber;

            isSendingRef.current = true;
            setIsLoading(true);
            setErrorMessage(null);
            setSuccessMessage(null);

            try {
                const result = await sendVerificationCode(
                    formData.action,
                    cardNumber,
                    formData.personalCode,
                    formData.phoneNumber,
                );

                if (result === FormStatus.Success) {
                    setPhase(Phase.Verification);
                } else {
                    if (result === FormStatus.AdminRestrictedError) {
                        setErrorMessage('auth.adminRestrictionError');
                    } else {
                        setErrorMessage('auth.twoFactorSendCodeError');
                    }
                }
            } catch {
                setErrorMessage('auth.twoFactorSendCodeError');
            } finally {
                setIsLoading(false);
                isSendingRef.current = false;
            }
        },
        [formData],
    );

    const handleVerify = useCallback(async () => {
        const cardNumber = formData.cardNumber.length === 6 ? `0${formData.cardNumber}` : formData.cardNumber;

        setIsLoading(true);
        setErrorMessage(null);
        setSuccessMessage(null);

        try {
            if (formData.action === AuthAction.Register) {
                const result = await registerWeakAuth(cardNumber, formData.personalCode, smsCode, formData.phoneNumber);

                if (result === FormStatus.Success) {
                    setPhase(Phase.Form);
                    setSmsCode('');
                    setFormData(prev => ({ ...prev, action: AuthAction.Login }));
                    setSuccessMessage('auth.registrationSuccess');
                } else {
                    setErrorMessage('auth.twoFactorVerifyError');
                }
            } else {
                const result = await loginWeakAuth(cardNumber, formData.personalCode, smsCode);

                if (result === FormStatus.Success) {
                    onSuccess?.();
                } else {
                    setErrorMessage('auth.twoFactorVerifyError');
                }
            }
        } catch {
            setErrorMessage('auth.twoFactorVerifyError');
        } finally {
            setIsLoading(false);
        }
    }, [formData, smsCode, onSuccess]);

    const handleCancelClick = useCallback(() => {
        handleCancel(null);
    }, [handleCancel]);

    const handleChangeAction = (value: string) => {
        setFormData(prev => ({ ...prev, action: value as AuthAction }));
    };

    const handleChangeCardNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
        const cardNumber = e.target.value.replace(/\D/g, '');

        setFormData(prev => ({ ...prev, cardNumber: cardNumber }));
    };

    const handleChangePersonalCode = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({ ...prev, personalCode: e.target.value }));
    };

    const handleChangePhoneNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({ ...prev, phoneNumber: e.target.value }));
    };

    const handleSmsCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSmsCode(e.target.value);
    };

    const isFormValid =
        (formData.cardNumber.length === 6 || formData.cardNumber.length === 7) &&
        formData.personalCode.trim() !== '' &&
        (formData.action === AuthAction.Login || formData.phoneNumber.trim() !== '');

    return (
        <>
            {phase === Phase.Form && (
                <div className="flex flex-col gap-8">
                    <div className="flex flex-col gap-6">
                        {successMessage == null && errorMessage == null && (
                            <Message variant="info">{t('auth.twoFactorInfoMessage')}</Message>
                        )}
                        {successMessage != null && <Message variant="success">{t(successMessage)}</Message>}
                        {errorMessage != null && <Message>{t(errorMessage)}</Message>}

                        <Select
                            name="action"
                            label={t('auth.selectActionLabel')}
                            options={[
                                {
                                    value: AuthAction.Login,
                                    label: t('auth.actionLogin'),
                                },
                                {
                                    value: AuthAction.Register,
                                    label: t('auth.actionRegister'),
                                },
                            ]}
                            value={formData.action}
                            onChange={handleChangeAction}
                        />

                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm">{t('auth.cardNumberLabel')}</label>
                            <div className="flex items-center gap-2">
                                <Paragraph>{t('auth.cardNumberPrefix')}</Paragraph>
                                <Input
                                    noSpaces
                                    className="w-full max-w-[85px]"
                                    name="cardNumber"
                                    placeholder={t('auth.cardNumberDigitsPlaceholder')}
                                    value={formData.cardNumber}
                                    maxLength={7}
                                    onChange={handleChangeCardNumber}
                                />
                            </div>
                        </div>

                        <Input
                            noSpaces
                            name="personalCode"
                            label={t('auth.idCodeLabel')}
                            placeholder={t('auth.idCodePlaceholder')}
                            value={formData.personalCode}
                            autoComplete={autoComplete}
                            disabledPasting={disablePasting}
                            onChange={handleChangePersonalCode}
                        />

                        {formData.action === AuthAction.Register && (
                            <Input
                                noSpaces
                                name="phoneNumber"
                                label={t('auth.phoneNumberLabel')}
                                placeholder={t('auth.phoneNumberPlaceholder')}
                                value={formData.phoneNumber}
                                autoComplete={autoComplete}
                                disabledPasting={disablePasting}
                                onChange={handleChangePhoneNumber}
                            />
                        )}
                    </div>

                    <Button onClick={handleSendCode} disabled={!isFormValid || isLoading}>
                        {isLoading ? (
                            <SpinnerIcon className="size-5 animate-spin" />
                        ) : formData.action === AuthAction.Login ? (
                            t('auth.login')
                        ) : (
                            t('auth.register')
                        )}
                    </Button>
                </div>
            )}

            {phase === Phase.Verification && (
                <div className="flex flex-col gap-8">
                    {errorMessage != null && <Message>{t(errorMessage)}</Message>}
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col justify-center gap-2 self-center p-4 text-center text-primary-500">
                            <SpinnerIcon className="size-8 animate-spin" />
                        </div>

                        <Paragraph isGray className="text-center">
                            {t('auth.twoFactorInstructions')}
                        </Paragraph>

                        <Input
                            noSpaces
                            name="smsCode"
                            label={t('auth.smsCodeLabel')}
                            placeholder={t('auth.smsCodePlaceholder')}
                            value={smsCode}
                            onChange={handleSmsCodeChange}
                        />
                    </div>

                    <div className="flex flex-col gap-3">
                        <Button onClick={handleVerify} disabled={smsCode.trim() === '' || isLoading}>
                            {isLoading ? (
                                <SpinnerIcon className="size-5 animate-spin" />
                            ) : formData.action === AuthAction.Login ? (
                                t('auth.login')
                            ) : (
                                t('auth.register')
                            )}
                        </Button>

                        <Button variant="outlined" color="neutral" onClick={handleCancelClick}>
                            {t('general.cancel')}
                        </Button>
                    </div>
                </div>
            )}
        </>
    );
}
